import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../LoginComp/firebase';
import jsPDF from 'jspdf';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
  LineChart, Line
} from "recharts";
import html2canvas from "html2canvas";
import "./admin-panel.css";

// ==================== CONSTANTS ====================
const ADMIN_PASSWORD = "Glaxmore@33";
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"];

const EXPENSE_TYPES = [
  "Accounting & Audit",
  "API & Third-party Services",
  "Backup & Recovery",
  "Bank Charges",
  "Bonuses & Incentives",
  "Cleaning & Security",
  "Client Entertainment",
  "Cloud Services (Google Cloud - Firebase)",
  "Compliance Costs",
  "Conferences & Seminars",
  "Consultant Fees",
  "Contractor Payments",
  "Corporate Gifts",
  "CRM & Marketing Tools",
  "Data Protection",
  "Deployment Services",
  "Development Tools",
  "Digital Marketing",
  "Domain",
  "Employee Benefits",
  "EPF & ETF Payments",
  "Events & Trade Shows",
  "Hardware Maintenance",
  "Hardware Purchases",
  "Hosting",
  "Hotel Accommodation",
  "Insurance Premiums",
  "Internet & Telephone",
  "IT Infrastructure",
  "Legal & Professional Fees",
  "Local Travel",
  "Maintenance & Repairs",
  "Marketing & Advertising",
  "Meeting Expenses",
  "Membership & Subscriptions",
  "Office Equipment",
  "Office Events",
  "Office Stationery Supplies",
  "Office Supplies",
  "Others",
  "Packaging Materials",
  "Paper & Envelopes",
  "Printing & Stationery",
  "Printer Cartridges & Toner",
  "Project Development Costs",
  "PVC & Acrylic Materials (NFC)",
  "Recruitment Expenses",
  "Rent & Utilities",
  "Salaries & Wages",
  "Security Services",
  "Security Software",
  "SEO & SEM Services",
  "Servers & Networking",
  "Software Licensing",
  "Subcontractor Services",
  "Support & Maintenance",
  "Tax Payments",
  "Testing & QA",
  "Training & Development",
  "Travel & Transportation",
  "UV Printing",
  "Vendor & Supplier Payments",
  "Writing Instruments & Notebooks"
];

// ==================== MAIN COMPONENT ====================
function AdminPanel() {
  // ==================== STATE MANAGEMENT ====================
  const [activeSubMenu, setActiveSubMenu] = useState('createVoucher');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [nextVoucherNumber, setNextVoucherNumber] = useState('001');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState('001');
  const [vendors, setVendors] = useState([]);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [vendorSearch, setVendorSearch] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState({ name: "" });
  const [selectedProject, setSelectedProject] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    year: "",
    month: "",
    branch: "",
    paymentMethod: "",
    name: "",
    project: "",
    voucherNumber: "",
    invoiceNumber: "",
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [pendingDate, setPendingDate] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingVoucher, setPendingVoucher] = useState(null);

  // ==================== FORM STATE ====================
  const [formData, setFormData] = useState({
    name: '',
    project: '',
    branch: '',
    description: '',
    amount: '',
    typeOfExpenses: '',
    paymentMethod: 'Cash',
    chequeNumber: '',
    depositNumber: '',
    date: new Date().toISOString().split('T')[0],
    preparedBy: '',
    authorizedBy: ''
  });

  const [vendorData, setVendorData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    address: ""
  });

  // ==================== LIFECYCLE METHODS ====================
  useEffect(() => {
    const getNextVoucherNumber = async () => {
      try {
        const q = query(
          collection(db, "vouchers"),
          orderBy("voucherNumber", "desc"),
          limit(1)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const lastVoucher = snapshot.docs[0].data();
          const nextNumber = (parseInt(lastVoucher.voucherNumber, 10) + 1).toString().padStart(3, '0');
          setNextVoucherNumber(nextNumber);
        } else {
          setNextVoucherNumber("001");
        }
      } catch (error) {
        console.error("Error getting next voucher number:", error);
        setNextVoucherNumber("001");
      }
    };

    getNextVoucherNumber();
  }, [vouchers]);

  useEffect(() => {
    const getNextInvoiceNumber = async () => {
      try {
        const q = query(
          collection(db, "invoices"),
          orderBy("invoiceNumber", "desc"),
          limit(1)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const lastInvoice = snapshot.docs[0].data();
          const nextNumber = (parseInt(lastInvoice.invoiceNumber, 10) + 1).toString().padStart(3, '0');
          setNextInvoiceNumber(nextNumber);
        } else {
          setNextInvoiceNumber("001");
        }
      } catch (error) {
        console.error("Error getting next invoice number:", error);
        setNextInvoiceNumber("001");
      }
    };

    getNextInvoiceNumber();
  }, [invoices]);

  useEffect(() => {
    const fetchProjects = async () => {
      const snapshot = await getDocs(collection(db, "projects"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(list);
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'vouchers'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const vouchersData = [];
      querySnapshot.forEach((doc) => {
        vouchersData.push({ id: doc.id, ...doc.data() });
      });
      setVouchers(vouchersData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching vouchers:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const invoicesData = [];
      querySnapshot.forEach((doc) => {
        invoicesData.push({ id: doc.id, ...doc.data() });
      });
      setInvoices(invoicesData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching invoices:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchVendors();
  }, []);

  // ==================== VENDOR FUNCTIONS ====================
  const fetchVendors = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "vendors"));
      const vendorList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const saveVendor = async () => {
    if (!vendorData.name) {
      alert("Vendor name is required");
      return;
    }

    try {
      if (editingVendorId) {
        const vendorRef = doc(db, "vendors", editingVendorId);
        await updateDoc(vendorRef, vendorData);
        setEditingVendorId(null);
      } else {
        await addDoc(collection(db, "vendors"), {
          ...vendorData,
          createdAt: new Date()
        });
      }

      setVendorData({ name: "", company: "", phone: "", email: "", address: "" });
      fetchVendors();
    } catch (error) {
      console.error("Error saving vendor:", error);
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await deleteDoc(doc(db, "vendors", id));
      fetchVendors();
    } catch (error) {
      console.error("Error deleting vendor:", error);
    }
  };

  const editVendor = (vendor) => {
    setVendorData({
      name: vendor.name,
      company: vendor.company,
      phone: vendor.phone,
      email: vendor.email,
      address: vendor.address
    });
    setEditingVendorId(vendor.id);
  };

  // ==================== PROJECT FUNCTIONS ====================
  const saveProject = async () => {
    if (!projectData.name) return alert("Project name required");

    await addDoc(collection(db, "projects"), {
      name: projectData.name,
      createdAt: new Date()
    });

    setProjectData({ name: "" });

    const snapshot = await getDocs(collection(db, "projects"));
    setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // ==================== VOUCHER FUNCTIONS ====================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      project: '',
      description: '',
      amount: '',
      typeOfExpenses: '',
      paymentMethod: 'Cash',
      date: new Date().toISOString().split('T')[0],
      preparedBy: '',
      authorizedBy: ''
    });
    setEditingVoucher(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let voucherNumber;

      if (editingVoucher) {
        voucherNumber = editingVoucher.voucherNumber;
      } else {
        voucherNumber = nextVoucherNumber;
      }

      let voucherData = {
        ...formData,
        voucherNumber,
        createdAt: editingVoucher ? editingVoucher.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingVoucher) {
        await updateDoc(doc(db, "vouchers", editingVoucher.id), voucherData);
      } else {
        const docRef = await addDoc(collection(db, "vouchers"), voucherData);
        voucherData.id = docRef.id;
        generatePDF(voucherData);
        setNextVoucherNumber((parseInt(voucherNumber, 10) + 1).toString().padStart(3, '0'));
      }

      resetForm();
      setLoading(false);
      setActiveSubMenu("viewVouchers");
    } catch (error) {
      console.error("Error saving voucher:", error);
      setLoading(false);
    }
  };

  // ==================== INVOICE FUNCTIONS ====================
  const createInvoiceFromVoucher = async (voucher) => {
    try {
      const invoiceNumber = nextInvoiceNumber;
      
      const invoiceData = {
        invoiceNumber: invoiceNumber,
        voucherNumber: voucher.voucherNumber,
        vendor: voucher.name,
        project: voucher.project || "",
        description: voucher.description,
        amount: voucher.amount,
        typeOfExpenses: voucher.typeOfExpenses,
        paymentMethod: voucher.paymentMethod,
        date: voucher.date,
        preparedBy: voucher.preparedBy,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'pending'
      };

      const docRef = await addDoc(collection(db, "invoices"), invoiceData);
      invoiceData.id = docRef.id;
      
      // Generate invoice PDF
      generateInvoicePDF(invoiceData);
      
      setNextInvoiceNumber((parseInt(invoiceNumber, 10) + 1).toString().padStart(3, '0'));
      
      alert(`Invoice #${invoiceNumber} created successfully!`);
      
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Error creating invoice. Please try again.");
    }
  };

  // ==================== INVOICE PDF GENERATION ====================
  const generateInvoicePDF = (invoice) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.setTextColor(0, 0, 0);

    // Company Header
    doc.setFontSize(20);
    doc.setFont("times", "bold");
    doc.text("GLAXMORE IT (PVT) LTD", 105, 25, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont("times", "normal");
    doc.text("No. 33, Sri Dharmarama Road, Ratmalana", 105, 33, { align: 'center' });
    doc.text("Reg No: PV00367232", 105, 39, { align: 'center' });
    doc.text("Tel: 070 1111 735 / 070 1111 738", 105, 45, { align: 'center' });

    // Divider
    doc.setDrawColor(0, 0, 0);
    doc.line(20, 50, 190, 50);

    // Invoice Title
    doc.setFontSize(18);
    doc.setFont("times", "bold");
    doc.text("TAX INVOICE", 105, 62, { align: 'center' });

    // Invoice Details Box
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(20, 70, 170, 35);

    doc.setFontSize(10);
    doc.setFont("times", "bold");
    doc.text("Invoice No:", 30, 80);
    doc.text("Date:", 30, 88);
    doc.text("Voucher No:", 30, 96);
    doc.text("Vendor:", 30, 104);

    doc.setFont("times", "normal");
    doc.text(`INV-${invoice.invoiceNumber}`, 70, 80);
    doc.text(invoice.date, 70, 88);
    doc.text(invoice.voucherNumber || "N/A", 70, 96);
    doc.text(invoice.vendor, 70, 104);

    // Project details
    if (invoice.project) {
      doc.setFont("times", "bold");
      doc.text("Project:", 120, 80);
      doc.setFont("times", "normal");
      doc.text(invoice.project, 155, 80);
    }

    // Table Header
    const tableTop = 115;
    doc.setFillColor(230, 230, 230);
    doc.rect(20, tableTop, 170, 8, 'F');
    
    doc.setFont("times", "bold");
    doc.setFontSize(10);
    doc.text("DESCRIPTION", 25, tableTop + 6);
    doc.text("AMOUNT (Rs.)", 180, tableTop + 6, { align: 'right' });

    // Table Content
    doc.setFont("times", "normal");
    const descLines = doc.splitTextToSize(invoice.description, 100);
    let descY = tableTop + 14;

    if (descLines.length > 1) {
      doc.text(descLines, 25, descY);
      const descHeight = descLines.length * 5;
      const amountY = descY + (descHeight / 2) + 1;
      doc.text(
        parseFloat(invoice.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }),
        180,
        amountY,
        { align: 'right' }
      );
      
      const totalY = descY + descHeight + 6;
      doc.line(20, totalY, 190, totalY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      doc.text("TOTAL", 25, totalY + 7);
      doc.text(
        `Rs. ${parseFloat(invoice.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        180,
        totalY + 7,
        { align: 'right' }
      );
      
      const sigY = totalY + 18;
      doc.line(20, sigY, 190, sigY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(10);
      doc.text("PREPARED BY", 40, sigY + 8);
      doc.text("AUTHORIZED BY", 140, sigY + 8);
      
      doc.setFont("times", "normal");
      doc.text(invoice.preparedBy || "______________", 40, sigY + 16);
      doc.text("______________", 140, sigY + 16);
      
      doc.line(28, sigY + 20, 82, sigY + 20);
      doc.line(128, sigY + 20, 182, sigY + 20);
      
      // Footer
      doc.setFont("times", "italic");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text("Thank you for your business!", 105, sigY + 32, { align: 'center' });
      
    } else {
      doc.text(invoice.description, 25, descY);
      doc.text(
        parseFloat(invoice.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }),
        180,
        descY,
        { align: 'right' }
      );
      
      const totalY = descY + 10;
      doc.line(20, totalY, 190, totalY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      doc.text("TOTAL", 25, totalY + 7);
      doc.text(
        `Rs. ${parseFloat(invoice.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        180,
        totalY + 7,
        { align: 'right' }
      );
      
      const sigY = totalY + 18;
      doc.line(20, sigY, 190, sigY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(10);
      doc.text("PREPARED BY", 40, sigY + 8);
      doc.text("AUTHORIZED BY", 140, sigY + 8);
      
      doc.setFont("times", "normal");
      doc.text(invoice.preparedBy || "______________", 40, sigY + 16);
      doc.text("______________", 140, sigY + 16);
      
      doc.line(28, sigY + 20, 82, sigY + 20);
      doc.line(128, sigY + 20, 182, sigY + 20);
      
      doc.setFont("times", "italic");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text("Thank you for your business!", 105, sigY + 32, { align: 'center' });
    }

    // Bottom border
    doc.line(20, 285, 190, 285);
    doc.line(20, 286, 190, 286);

    doc.save(`invoice_INV-${invoice.invoiceNumber}.pdf`);
  };

  const handleEditRequest = (voucher) => {
    setPendingAction("edit");
    setPendingVoucher(voucher);
    setShowPasswordModal(true);
  };

  const handleDeleteRequest = (voucher) => {
    setPendingAction("delete");
    setPendingVoucher(voucher);
    setShowPasswordModal(true);
  };

  const generatePDF = (voucher) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a5'
    });

    doc.setTextColor(0, 0, 0);

    const logoImg = new Image();
    logoImg.src = "/logo192.png";

    logoImg.onload = () => {
      doc.addImage(logoImg, "PNG", 25, 5, 22, 22);
    };

    const logoX = 35;
    const logoY = 5;
    const logoSize = 22;

    try {
      doc.addImage(logoImg, "PNG", logoX, logoY, logoSize, logoSize);
    } catch (e) {
      doc.setFontSize(20);
      doc.setFont("times", "bold");
      doc.text("GLAXMORE IT", logoX + 2, logoY + 12);
    }

    const textX = logoX + logoSize + 10;

    doc.setFontSize(18);
    doc.setFont("times", "bold");
    doc.text("GLAXMORE IT (PVT) LTD", textX, 12);

    doc.setFontSize(11);
    doc.setFont("times", "normal");
    doc.text("No. 33, Sri Dharmarama Road, Ratmalana", textX, 19);
    doc.text("Reg No. PV00367232", textX, 25);
    doc.text("Tel: 070 1111 735 / 070 1111 738", textX, 30);

    doc.setDrawColor(0, 0, 0);
    doc.line(15, 32, 195, 32);
    doc.line(15, 33, 195, 33);

    doc.setFontSize(14);
    doc.setFont("times", "bold");
    doc.text("VOUCHER", 105, 42, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont("times", "normal");

    doc.text(`Voucher No: ${voucher.voucherNumber}`, 20, 50);
    doc.text(`Date: ${voucher.date}`, 20, 57);
    doc.text(`Vendor: ${voucher.name}`, 20, 64);

    doc.text(`Expense Type: ${voucher.typeOfExpenses}`, 120, 50);
    
    let paymentDetails = `Payment: ${voucher.paymentMethod}`;
    if (voucher.paymentMethod === "Cheque" && voucher.chequeNumber) {
      paymentDetails += ` (Chq: ${voucher.chequeNumber})`;
    }
    if (voucher.paymentMethod === "Direct Deposit" && voucher.depositNumber) {
      paymentDetails += ` (Dep: ${voucher.depositNumber})`;
    }
    doc.text(paymentDetails, 120, 57);

    if (voucher.project) {
      doc.text(`Project: ${voucher.project}`, 120, 64);
    }

    const tableTop = 72;
    
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    
    doc.setFont("times", "bold");
    doc.setFontSize(10);
    doc.text("DESCRIPTION", 20, tableTop + 6);
    doc.text("AMOUNT (Rs.)", 175, tableTop + 6, { align: 'right' });

    doc.setFont("times", "normal");
    doc.setFontSize(10);
    
    const descriptionLines = doc.splitTextToSize(voucher.description, 120);
    let descriptionY = tableTop + 12;
    let rowHeight = 5;

    if (descriptionLines.length > 1) {
      doc.text(descriptionLines, 20, descriptionY);
      const descriptionHeight = descriptionLines.length * rowHeight;
      const amountY = descriptionY + (descriptionHeight / 2) + 1;
      doc.text(
        parseFloat(voucher.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }), 
        175, 
        amountY, 
        { align: 'right' }
      );
      
      const totalStartY = descriptionY + descriptionHeight + 6;
      
      doc.setDrawColor(0, 0, 0);
      doc.line(15, totalStartY, 195, totalStartY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.text("TOTAL", 20, totalStartY + 6);
      doc.text(
        `Rs. ${parseFloat(voucher.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 
        175, 
        totalStartY + 6, 
        { align: 'right' }
      );
      
      const signaturesY = totalStartY + 16;
      
      doc.setDrawColor(0, 0, 0);
      doc.line(15, signaturesY, 195, signaturesY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(10);
      doc.text("PREPARED BY", 40, signaturesY + 8);
      
      doc.setFont("times", "normal");
      doc.text(voucher.preparedBy || "______________", 40, signaturesY + 16);
      
      doc.line(28, signaturesY + 20, 82, signaturesY + 20);
      
      doc.setFont("times", "italic");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text("Your Partner In Smarter IT Innovation", 105, signaturesY + 30, { align: 'center' });
      
    } else {
      doc.text(voucher.description, 20, descriptionY);
      doc.text(
        parseFloat(voucher.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }), 
        175, 
        descriptionY, 
        { align: 'right' }
      );
      
      const totalStartY = descriptionY + 8;
      
      doc.setDrawColor(0, 0, 0);
      doc.line(15, totalStartY, 195, totalStartY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.text("TOTAL", 20, totalStartY + 6);
      doc.text(
        `Rs. ${parseFloat(voucher.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 
        175, 
        totalStartY + 6, 
        { align: 'right' }
      );
      
      const signaturesY = totalStartY + 16;
      
      doc.setDrawColor(0, 0, 0);
      doc.line(15, signaturesY, 195, signaturesY);
      
      doc.setFont("times", "bold");
      doc.setFontSize(10);
      doc.text("PREPARED BY", 40, signaturesY + 8);
      
      doc.setFont("times", "normal");
      doc.text(voucher.preparedBy || "______________", 40, signaturesY + 16);
      
      doc.line(28, signaturesY + 20, 82, signaturesY + 20);
      
      doc.setFont("times", "italic");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text("Your Partner In Smarter IT Innovation", 105, signaturesY + 30, { align: 'center' });
    }

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(15, 198, 195, 198);
    doc.line(15, 199, 195, 199);

    doc.save(`voucher_${voucher.voucherNumber}.pdf`);
  };

  const downloadChartAsPDF = (id, filename) => {
    const chartElement = document.getElementById(id);
    if (!chartElement) return;
    
    html2canvas(chartElement).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "mm", "a4");
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(filename);
    });
  };

  const exportToExcel = () => {
    const dataToExport = filteredVouchers; 

    if (!dataToExport || dataToExport.length === 0) {
      alert("No filtered data to export!");
      return;
    }

    const formattedData = dataToExport.map(v => ({
      "Date": v.date,
      "Voucher Number": v.voucherNumber,
      "Vendor": v.name,
      "Branch": v.branch,
      "Type Of Expenses": v.typeOfExpenses,
      "Description": v.description,
      "Amount (Rs.)": v.amount,
      "Payment Method": v.paymentMethod,
      "Cheque Number": v.chequeNumber,
      "Project": v.project || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");

    XLSX.writeFile(workbook, "Vouchers.xlsx");
  };

  const handleLogout = () => {
    navigate('/');
  };

  // ==================== COMPUTED PROPERTIES ====================
  const filteredVouchers = vouchers.filter((v) => {
    const voucherDate = new Date(v.date);
    const voucherYear = voucherDate.getFullYear().toString();
    const voucherMonth = (voucherDate.getMonth() + 1)
      .toString()
      .padStart(2, "0");

    const projectValue = v.project || "NO_PROJECT";

    return (
      (!filters.year || filters.year === voucherYear) &&
      (!filters.month || filters.month === voucherMonth) &&
      (!filters.paymentMethod || filters.paymentMethod === v.paymentMethod) &&
      (!filters.name || filters.name === v.name) &&
      (!filters.project || filters.project === projectValue) &&
      (!filters.voucherNumber ||
        v.voucherNumber
          ?.toString()
          .toLowerCase()
          .includes(filters.voucherNumber.toLowerCase()))
    );
  });

  const groupedByProject = {};

  filteredVouchers.forEach(v => {
    const key = v.project || "NO_PROJECT";

    if (!groupedByProject[key]) {
      groupedByProject[key] = {
        total: 0,
        vouchers: []
      };
    }

    groupedByProject[key].total += parseFloat(v.amount || 0);
    groupedByProject[key].vouchers.push(v);
  });

  // ==================== RENDER FUNCTIONS ====================
  const renderCreateVoucher = () => {
    const isVendorSelected = formData.name && formData.name.trim() !== "";

    return (
      <div className="content-section">
        <h2>{editingVoucher ? 'Edit Voucher' : 'Create Voucher'}</h2>

        <div className="input-group">
          <label>Project (Optional)</label>
          <select
            name="project"
            value={formData.project}
            onChange={handleInputChange}
          >
            <option value="">-- No Project --</option>
            {projects.map(p => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label>Vendor *</label>
          <input
            type="text"
            placeholder="Search vendor..."
            value={formData.name || vendorSearch}
            onChange={(e) => {
              setVendorSearch(e.target.value);
              setShowVendorList(true);
            }}
            onFocus={() => setShowVendorList(true)}
          />

          {showVendorList && (
            <div className="vendor-dropdown">
              {vendors
                .filter(v =>
                  v.name?.toLowerCase().includes(vendorSearch.toLowerCase())
                )
                .map((vendor) => (
                  <div
                    key={vendor.id}
                    className="vendor-option"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        name: vendor.name
                      }));

                      setVendorSearch(vendor.name);
                      setShowVendorList(false);
                    }}
                  >
                    {vendor.name} {vendor.company && `- ${vendor.company}`}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="voucher-number-display">
          Voucher Number: {editingVoucher ? editingVoucher.voucherNumber : nextVoucherNumber}
        </div>
        <form className="form-container" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="input-group">
              <label>Description *</label>
              <input 
                type="text" 
                name="description"
                placeholder="Enter description" 
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="input-group">
              <label>Amount (Rs.) *</label>
              <input 
                type="number" 
                name="amount"
                placeholder="Enter amount" 
                value={formData.amount}
                onChange={handleInputChange}
                required
                step="0.01"
                min="0"
              />
            </div>

            <div className="input-group">
              <label>Type Of Expenses *</label>
              <select
                name="typeOfExpenses"
                value={formData.typeOfExpenses}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Select Expense Type --</option>
                {EXPENSE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label>Payment Method *</label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                required
              >
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Direct Deposit">Direct Deposit</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {formData.paymentMethod === "Cheque" && (
              <div className="input-group">
                <label>Cheque Number *</label>
                <input
                  type="text"
                  name="chequeNumber"
                  value={formData.chequeNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            {formData.paymentMethod === "Direct Deposit" && (
              <div className="input-group">
                <label>Deposit Number *</label>
                <input
                  type="text"
                  name="depositNumber"
                  value={formData.depositNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            <div className="input-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={(e) => {
                  const today = new Date().toISOString().split("T")[0];
                  if (e.target.value < today) {
                    setPendingAction("date");
                    setPendingDate(e.target.value);
                    setShowPasswordModal(true);
                    return;
                  }
                  handleInputChange(e);
                }}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label>Prepared By *</label>
              <input 
                type="text" 
                name="preparedBy"
                placeholder="Enter name" 
                value={formData.preparedBy}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="input-group">
              <label>Authorized By</label>
              <input 
                type="text" 
                name="authorizedBy"
                placeholder="Enter name" 
                value={formData.authorizedBy}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-buttons">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !isVendorSelected}
            >
              {loading
                ? "Saving..."
                : editingVoucher
                ? "Update Voucher"
                : "Create Voucher & Download PDF"}
            </button>
            {editingVoucher && (
              <button 
                type="button" 
                className="cancel-btn"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>
    );
  };

  const renderViewVouchers = () => (
    <div className="content-section">
      <h2>View All Vouchers</h2>

      <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
        <i className="fas fa-filter"></i> {showFilters ? "Hide Filters" : "Show Filters"}
      </button>

      {showFilters && (
        <div className="filter-section">
          <input
            type="text"
            name="voucherNumber"
            placeholder="Search Voucher Number"
            value={filters.voucherNumber}
            onChange={handleFilterChange}
          />

          <select name="year" value={filters.year} onChange={handleFilterChange}>
            <option value="">All Years</option>
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>

          <select name="month" value={filters.month} onChange={handleFilterChange}>
            <option value="">All Months</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m,i) => (
              <option key={i} value={m}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>

          <select name="paymentMethod" value={filters.paymentMethod} onChange={handleFilterChange}>
            <option value="">All Methods</option>
            <option value="Cash">Cash</option>
            <option value="Cheque">Cheque</option>
            <option value="Direct Deposit">Direct Deposit</option>
            <option value="Other">Other</option>
          </select>

          <select name="project" value={filters.project} onChange={handleFilterChange}>
            <option value="">All Projects</option>
            <option value="NO_PROJECT">No Project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>

          <select name="name" value={filters.name} onChange={handleFilterChange}>
            <option value="">All Vendors</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.name}>
                {vendor.name} {vendor.company && `- ${vendor.company}`}
              </option>
            ))}
          </select>

          <button
            className="reset-btn"
            onClick={() => setFilters({ year: "", month: "", paymentMethod: "", name: "", voucherNumber: "" })}
          >
            <i className="fas fa-undo"></i> Reset Filters
          </button>

          <button className="export-btn" onClick={exportToExcel}>
            <i className="fas fa-file-excel"></i> Export Excel
          </button>
        </div>
      )}

      {loading ? (
        <p>Loading vouchers...</p>
      ) : vouchers.length === 0 ? (
        <p>No vouchers found. Create your first voucher.</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Voucher Number</th>
                <th>Name</th>
                <th>Description</th>
                <th>Amount (Rs.)</th>
                <th>Date</th>
                <th>Payment Method</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {Object.entries(groupedByProject).map(([project, data]) => (
                <React.Fragment key={project}>
                  <tr style={{ background: "#f1f1f1", fontWeight: "bold" }}>
                    <td colSpan="7">
                      {project === "NO_PROJECT" ? "No Project" : `Project: ${project}`} 
                      {" | "} Total: Rs. {data.total.toLocaleString()}
                    </td>
                  </tr>

                  {data.vouchers.map(voucher => (
                    <tr key={voucher.id}>
                      <td>{voucher.voucherNumber}</td>
                      <td>{voucher.name}</td>
                      <td>{voucher.description}</td>
                      <td>{parseFloat(voucher.amount).toLocaleString()}</td>
                      <td>{voucher.date}</td>
                      <td>{voucher.paymentMethod}</td>
                      <td>
                        <button onClick={() => generatePDF(voucher)}>PDF</button>
                        <button 
                          onClick={() => createInvoiceFromVoucher(voucher)}
                          style={{ background: '#1a2a4a', marginLeft: '4px' }}
                        >
                          Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAnalysis = () => {
    const monthlyTypeData = {};
    vouchers.forEach((v) => {
      const d = new Date(v.date);
      const month = d.toLocaleString("default", { month: "short" });
      const year = d.getFullYear();
      const key = `${month}-${year}`;

      if (!monthlyTypeData[key]) monthlyTypeData[key] = {};
      monthlyTypeData[key][v.typeOfExpenses] =
        (monthlyTypeData[key][v.typeOfExpenses] || 0) + parseFloat(v.amount || 0);
    });

    const monthlyPieCharts = Object.entries(monthlyTypeData).map(([monthYear, types]) => {
      const pieData = Object.entries(types).map(([name, value]) => ({ name, value }));
      return { monthYear, pieData };
    });

    const yearlyData = {};
    vouchers.forEach((v) => {
      const d = new Date(v.date);
      const year = d.getFullYear();
      yearlyData[year] = (yearlyData[year] || 0) + parseFloat(v.amount || 0);
    });
    const yearlyChartData = Object.entries(yearlyData).map(([y, amt]) => ({ year: y, amount: amt }));

    return (
      <div className="content-section">
        <h2>Analysis Report</h2>

        {monthlyPieCharts.map(({ monthYear, pieData }, idx) => (
          <details key={idx} className="month-section" open={idx === monthlyPieCharts.length - 1}>
            <summary className="month-title">{monthYear} Expenses by Type</summary>
            <div id={`pieChart-${monthYear}`} style={{ textAlign: "center", marginBottom: "20px" }}>
              <PieChart width={700} height={400}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  fill="#8884d8"
                  label
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
              <button onClick={() => downloadChartAsPDF(`pieChart-${monthYear}`, `${monthYear}-expenses.pdf`)}>
                Download PDF
              </button>
            </div>
          </details>
        ))}

        <div id="yearlyChart" style={{ textAlign: "center", marginTop: "30px" }}>
          <h3>Yearly Expenses Trend</h3>
          <LineChart width={700} height={300} data={yearlyChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="amount" stroke="#82ca9d" />
          </LineChart>
          <button onClick={() => downloadChartAsPDF("yearlyChart", "yearly-expenses.pdf")}>
            Download PDF
          </button>
        </div>
      </div>
    );
  };

  const renderAddVendor = () => (
    <div className="vendor-main-container">
      <div className="add-vendor-container">
        <h2>{editingVendorId ? "Edit Vendor" : "Add Vendor"}</h2>
        <div className="vendor-form">
          <input
            type="text"
            placeholder="Vendor Name *"
            value={vendorData.name}
            onChange={(e) => setVendorData({ ...vendorData, name: e.target.value })}
          />
          <input
            type="text"
            placeholder="Company"
            value={vendorData.company}
            onChange={(e) => setVendorData({ ...vendorData, company: e.target.value })}
          />
          <input
            type="text"
            placeholder="Phone"
            value={vendorData.phone}
            onChange={(e) => setVendorData({ ...vendorData, phone: e.target.value })}
          />
          <input
            type="email"
            placeholder="Email"
            value={vendorData.email}
            onChange={(e) => setVendorData({ ...vendorData, email: e.target.value })}
          />
          <textarea
            placeholder="Address"
            value={vendorData.address}
            onChange={(e) => setVendorData({ ...vendorData, address: e.target.value })}
          />
          <button className="save-vendor-btn" onClick={saveVendor}>
            {editingVendorId ? "Update Vendor" : "Save Vendor"}
          </button>
          {editingVendorId && (
            <button className="cancel-btn" onClick={() => {
              setEditingVendorId(null);
              setVendorData({ name: "", company: "", phone: "", email: "", address: "" });
            }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="vendor-list-container">
        <h3>All Vendors</h3>
        <div className="vendor-scroll-list">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="vendor-card">
              <div className="vendor-info">
                <b>{vendor.name}</b>
                {vendor.company && <p>{vendor.company}</p>}
                {vendor.phone && <p>{vendor.phone}</p>}
                {vendor.email && <p>{vendor.email}</p>}
                {vendor.address && <p>{vendor.address}</p>}
              </div>
              <div className="vendor-actions">
                <button className="edit-btn" onClick={() => editVendor(vendor)}>
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAddProject = () => (
    <div className="add-project-container">
      <h2>Add Project</h2>

      <input
        type="text"
        placeholder="Enter Project Name"
        value={projectData.name}
        onChange={(e) => setProjectData({ name: e.target.value })}
      />

      <button className="save-project-btn" onClick={saveProject}>
        Add Project
      </button>

      <div className="project-list">
        <h3>All Projects</h3>

        {projects.map((p) => (
          <div key={p.id} className="project-item">
            <span className="project-name">{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderWelcome = () => (
    <div className="content-section welcome">
      <h2>Welcome to GLAXMORE IT - Internal Voucher Admin Panel</h2>
      <p>Select an option from the sidebar to manage vouchers.</p>
    </div>
  );

  const renderMainContent = () => {
    switch(activeSubMenu) {
      case 'addProject':
        return renderAddProject();
      case 'createVoucher':
        return renderCreateVoucher();
      case 'viewVouchers':
        return renderViewVouchers();
      case 'analysis':
        return renderAnalysis();
      case 'addVendor':
        return renderAddVendor();
      default:
        return renderWelcome();
    }
  };

  // ==================== RENDER COMPONENT ====================
  return (
    <div className="admin-panel-institute">
      <div className="top-nav">
        <div className="nav-buttons">
          <button 
            className={activeSubMenu === 'addVendor' ? 'active' : ''}
            onClick={() => setActiveSubMenu('addVendor')}
          >
            <i className="fas fa-plus-circle"></i> Add Vendor
          </button>

          <button 
            className={activeSubMenu === 'addProject' ? 'active' : ''}
            onClick={() => setActiveSubMenu('addProject')}
          >
            Add Project
          </button>

          <button 
            className={activeSubMenu === 'createVoucher' ? 'active' : ''}
            onClick={() => setActiveSubMenu('createVoucher')}
          >
            <i className="fas fa-plus-circle"></i> Create Voucher
          </button>
          
          <button 
            className={activeSubMenu === 'viewVouchers' ? 'active' : ''}
            onClick={() => setActiveSubMenu('viewVouchers')}
          >
            <i className="fas fa-list"></i> View All Vouchers
          </button>

          <button 
            className={activeSubMenu === 'analysis' ? 'active' : ''}
            onClick={() => setActiveSubMenu('analysis')}
          >
            <i className="fas fa-chart-bar"></i> Analysis Report
          </button>
        </div>
        
        <button className="logout-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i> Logout
        </button>

        {showPasswordModal && (
          <div className="modal-overlay">
            <div className="modal-box">
              <h3>Enter Admin Password</h3>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Password"
                autoFocus
              />
              <div className="modal-actions">
                <button
                  onClick={async () => {
                    if (passwordInput === ADMIN_PASSWORD) {
                      if (pendingAction === "date") {
                        handleInputChange({ target: { name: "date", value: pendingDate } });
                      } else if (pendingAction === "edit" && pendingVoucher) {
                        setEditingVoucher(pendingVoucher);
                        setFormData({
                          description: pendingVoucher.description,
                          amount: pendingVoucher.amount,
                          typeOfExpenses: pendingVoucher.typeOfExpenses,
                          paymentMethod: pendingVoucher.paymentMethod,
                          chequeNumber: pendingVoucher.chequeNumber || '',
                          depositNumber: pendingVoucher.depositNumber || '',
                          date: pendingVoucher.date,
                          preparedBy: pendingVoucher.preparedBy,
                          authorizedBy: pendingVoucher.authorizedBy,
                        });
                        setActiveSubMenu("createVoucher");
                      } else if (pendingAction === "delete" && pendingVoucher) {
                        if (window.confirm("Are you sure you want to delete this voucher?")) {
                          try {
                            await deleteDoc(doc(db, "vouchers", pendingVoucher.id));
                          } catch (error) {
                            console.error("Error deleting voucher:", error);
                          }
                        }
                      }

                      setShowPasswordModal(false);
                      setPasswordInput("");
                      setPendingAction(null);
                      setPendingVoucher(null);
                      setPendingDate("");
                    } else {
                      alert("Wrong Password!!!");
                    }
                  }}
                >
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordInput("");
                    setPendingAction(null);
                    setPendingVoucher(null);
                    setPendingDate("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="main-content">
        {renderMainContent()}
      </div>
    </div>
  );
}

export default AdminPanel;